import { useState, useRef, useCallback } from 'react';
import { Upload, Check, X, File as FileIcon, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { uploadFile, type UploadedFileMetadata } from '@/lib/api/upload';

interface FileUploadConfig {
  maxFiles?: number;
  maxSizeBytes?: number;
  allowedTypes?: string[];
}

interface InlineFileUploadProps {
  questionRef: string;
  config?: FileUploadConfig;
  onUpload: (files: UploadedFileMetadata[]) => void;
  disabled?: boolean;
  sessionToken?: string;
}

interface UploadingFile {
  id: string;
  file: File;
  localPreview?: string;
  progress: number;
}

interface UploadedFile extends UploadedFileMetadata {
  localPreview?: string;
}

function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const size = bytes / Math.pow(1024, index);
  return `${Math.round(size * 10) / 10} ${units[index]}`;
}

function isImageType(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

/**
 * Inline file upload component for chat interface.
 * Integrates with R2 storage via uploadFile API.
 */
export function InlineFileUpload({
  config,
  onUpload,
  disabled = false,
  sessionToken,
}: InlineFileUploadProps) {
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isUploading = uploadingFiles.length > 0;

  const validateFile = useCallback(
    (file: File): string | null => {
      // Check file size
      if (config?.maxSizeBytes && file.size > config.maxSizeBytes) {
        const maxSizeMb = Math.round(config.maxSizeBytes / (1024 * 1024));
        return `File "${file.name}" exceeds ${maxSizeMb}MB limit`;
      }

      // Check file type
      if (config?.allowedTypes && config.allowedTypes.length > 0) {
        const isAllowed = config.allowedTypes.some((type) => {
          if (type.endsWith('/*')) {
            return file.type.startsWith(type.replace('/*', ''));
          }
          return file.type === type || file.name.toLowerCase().endsWith(type.toLowerCase());
        });

        if (!isAllowed) {
          return `File type "${file.type || 'unknown'}" is not allowed`;
        }
      }

      return null;
    },
    [config]
  );

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      if (files.length === 0) return;

      setError(null);

      // Check max files limit
      const maxFiles = config?.maxFiles ?? 10;
      const totalFiles = uploadedFiles.length + files.length;
      if (totalFiles > maxFiles) {
        setError(`Maximum ${maxFiles} file(s) allowed`);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      // Validate all files first
      for (const file of files) {
        const validationError = validateFile(file);
        if (validationError) {
          setError(validationError);
          if (fileInputRef.current) fileInputRef.current.value = '';
          return;
        }
      }

      // Create uploading file entries with local previews
      const newUploadingFiles: UploadingFile[] = files.map((file) => ({
        id: crypto.randomUUID(),
        file,
        localPreview: isImageType(file.type) ? URL.createObjectURL(file) : undefined,
        progress: 0,
      }));

      setUploadingFiles((prev) => [...prev, ...newUploadingFiles]);

      // Upload each file
      for (const uploadingFile of newUploadingFiles) {
        try {
          const metadata = await uploadFile(uploadingFile.file, {
            onProgress: (progress) => {
              setUploadingFiles((prev) =>
                prev.map((f) => (f.id === uploadingFile.id ? { ...f, progress } : f))
              );
            },
            sessionToken,
          });

          // Move from uploading to uploaded
          setUploadingFiles((prev) => prev.filter((f) => f.id !== uploadingFile.id));
          setUploadedFiles((prev) => [
            ...prev,
            { ...metadata, localPreview: uploadingFile.localPreview },
          ]);
        } catch (err) {
          // Remove from uploading on error
          setUploadingFiles((prev) => prev.filter((f) => f.id !== uploadingFile.id));
          // Clean up local preview
          if (uploadingFile.localPreview) {
            URL.revokeObjectURL(uploadingFile.localPreview);
          }
          setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
        }
      }

      // Reset input
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [config, uploadedFiles.length, validateFile, sessionToken]
  );

  const handleRemove = useCallback((index: number) => {
    setUploadedFiles((prev) => {
      const file = prev[index];
      // Clean up local preview URL
      if (file?.localPreview) {
        URL.revokeObjectURL(file.localPreview);
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleSubmit = useCallback(() => {
    if (uploadedFiles.length > 0) {
      // Send full metadata (excluding localPreview which is just for UI)
      onUpload(uploadedFiles.map(({ localPreview, ...metadata }) => metadata));
    }
  }, [uploadedFiles, onUpload]);

  const acceptTypes = config?.allowedTypes?.join(',');
  const isMultiple = config?.maxFiles !== 1;

  return (
    <div className="flex flex-col gap-3">
      <input
        ref={fileInputRef}
        type="file"
        multiple={isMultiple}
        accept={acceptTypes}
        onChange={handleFileChange}
        disabled={disabled || isUploading}
        className="hidden"
      />

      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled || isUploading}
        className={cn(
          'flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border px-6 py-4',
          'text-sm text-muted-foreground transition-colors',
          'hover:border-primary/50 hover:bg-accent/50',
          'focus:outline-none focus:ring-2 focus:ring-primary/20',
          'disabled:cursor-not-allowed disabled:opacity-50'
        )}
      >
        <Upload className="size-5" />
        {isUploading ? 'Uploading...' : 'Choose Files'}
      </button>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Uploading files with progress */}
      {uploadingFiles.length > 0 && (
        <div className="flex flex-col gap-2">
          {uploadingFiles.map((file) => (
            <div key={file.id} className="flex flex-col gap-2 rounded-lg bg-muted px-3 py-2">
              <div className="flex items-center gap-3">
                {file.localPreview ? (
                  <img
                    src={file.localPreview}
                    alt={`Uploading ${file.file.name}`}
                    className="size-10 rounded border border-border object-cover"
                  />
                ) : (
                  <FileIcon className="size-5 text-muted-foreground" />
                )}
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium">{file.file.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {formatFileSize(file.file.size)} • {file.progress}%
                  </span>
                </div>
                <Loader2 className="size-4 animate-spin text-primary" />
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted-foreground/20">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-200"
                  style={{ width: `${file.progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Uploaded files with preview */}
      {uploadedFiles.length > 0 && (
        <div className="flex flex-col gap-2">
          {uploadedFiles.map((file, index) => (
            <div
              key={file.fileKey}
              className="flex items-center gap-3 rounded-lg bg-muted px-3 py-2"
            >
              {isImageType(file.mimeType) ? (
                <img
                  src={file.localPreview || file.url}
                  alt={`Preview of ${file.fileName}`}
                  className="size-12 rounded border border-border object-cover"
                />
              ) : (
                <div className="flex size-12 items-center justify-center rounded border border-border bg-background">
                  <FileIcon className="size-5 text-muted-foreground" />
                </div>
              )}
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-medium">{file.fileName}</span>
                <span className="text-xs text-muted-foreground">{formatFileSize(file.fileSize)}</span>
                <div className="flex items-center gap-1 text-xs text-green-600">
                  <Check className="size-3" />
                  <span>Uploaded</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRemove(index)}
                disabled={disabled}
                className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50"
                aria-label={`Remove ${file.fileName}`}
              >
                <X className="size-4" />
              </button>
            </div>
          ))}

          <Button
            onClick={handleSubmit}
            disabled={disabled || isUploading}
            size="sm"
            className="self-start"
          >
            Continue
          </Button>
        </div>
      )}
    </div>
  );
}
